import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { FormFieldComponent } from '../form-field/form-field.component';
import { ITrackingAction, ITrackingObject } from 'i2g-ng-tracking';
import { IFormFieldErrorMessages } from '../../form-master.interface';
import {
  IAutoCompleteOnFetchEnd,
  IAutoCompleteOnSet,
  IAutoCompleteOptions,
} from '../../../../directives/auto-complete/auto-complete.interface';

@Component({
  selector: 'i2g-textarea-field',
  templateUrl: 'text-area-field.component.html',
  styleUrls: ['text-area-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextAreaFieldComponent
  extends FormFieldComponent
  implements OnInit {
  @Input() label: string;
  @Input() showLabel = true;
  @Input() fieldId: string;
  @Input() placeholder: string;
  @Input() helpText: string;
  @Input() fieldControl: AbstractControl;
  @Input() focusableTrigger$: Observable<void>;
  @Input() isDisabled = false;
  @Input() readOnly: boolean;
  @Input() textareaE2eId: string;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingAction: ITrackingAction;
  @Input() errorMessages: IFormFieldErrorMessages;
  /**
   * The maxlength attribute here is not used on the actual textarea element
   * as providing that attribute triggers the default HTML5
   * whereby it will not accept characters that go over the specified length,
   * even if it is pasted in.
   *
   * Our design system operates on the idea of showing user the error
   * rather than trimming the content user had pasted.
   * Therefore, the attribute here is only used for showing the length in the UI
   * but NOT to actually enforce the limit via native HTML5.
   */
  @Input() maxlength = 3000;
  @Input() rows = 3;

  /**
   * Determine if char counter should be displayed
   */
  @Input() showCounter = true;
  /**
   * Determine if textarea height should grow dynamically based on its value
   */
  @Input() dynamicResize = false;
  @Input() removeNativeAutocomplete = false;
  @Input() autoCompleteOptions: IAutoCompleteOptions;
  /**
   * Expand width and height of textarea 100% based on parent size
   */
  @Input() responsive = false;

  @Output() textAreaFocus: EventEmitter<string> = new EventEmitter<string>();
  @Output() textAreaBlur: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  autoCompleteFetchStart: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  autoCompleteFetchEnd: EventEmitter<IAutoCompleteOnFetchEnd> = new EventEmitter<IAutoCompleteOnFetchEnd>();
  @Output()
  autoCompleteSet: EventEmitter<IAutoCompleteOnSet> = new EventEmitter<IAutoCompleteOnSet>();
  @Output() autoCompleteHide: EventEmitter<void> = new EventEmitter<void>();
  @Output() autoCompleteDestroy: EventEmitter<void> = new EventEmitter<void>();

  hasFocus$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    _changeDetectorRef: ChangeDetectorRef,
    translateService: TranslateService
  ) {
    super(_changeDetectorRef, translateService);
  }

  ngOnInit() {
    super.ngOnInit();
  }

  handleFocus() {
    this.textAreaFocus.emit(this.fieldControl.value);

    this.hasFocus$.next(true);
  }

  handleBlur() {
    this.textAreaBlur.emit(this.fieldControl.value);

    this.hasFocus$.next(false);
  }

  get hasError() {
    return (
      !!this.errorMessage &&
      this.fieldControl.dirty &&
      this.fieldControl.invalid
    );
  }
}
